/**
 * InvoiceViewTotals
 *
 * Renders the totals table: taxable amount, GST breakdown, and total amount.
 * Also renders amount-in-words and bank details sections.
 */

import React from 'react'

import { formatAmount } from '../../../types/billing'

import type { TaxBreakdownEntry } from './invoice-view-helpers'
import type { Invoice } from '../../../types/billing'

// =============================================================================
// Tax breakdown rows
// =============================================================================

interface TaxRowsProps {
  taxBreakdown: TaxBreakdownEntry[]
  isIntrastate: boolean
  currency: string | undefined
}

function TaxRows({ taxBreakdown, isIntrastate, currency }: TaxRowsProps): JSX.Element {
  return (
    <>
      {taxBreakdown.map(({ rate, cgst, sgst, igst }) =>
        isIntrastate ? (
          <React.Fragment key={rate}>
            <tr>
              <td>CGST {rate / 2}%:</td>
              <td className="amount">{formatAmount(cgst, currency)}</td>
            </tr>
            <tr>
              <td>SGST {rate / 2}%:</td>
              <td className="amount">{formatAmount(sgst, currency)}</td>
            </tr>
          </React.Fragment>
        ) : (
          <tr key={rate}>
            <td>IGST {rate}%:</td>
            <td className="amount">{formatAmount(igst, currency)}</td>
          </tr>
        ),
      )}
    </>
  )
}

// =============================================================================
// Bank details section
// =============================================================================

interface BankDetailRowProps {
  label: string
  value: string | undefined
  show?: boolean
}

function BankDetailRow({ label, value, show = true }: BankDetailRowProps): JSX.Element | null {
  if (!show || !value) { return null }
  return (
    <div className="bank-detail-row">
      <span className="bank-label">{label}:</span>
      <span className="bank-value">{value}</span>
    </div>
  )
}

interface BankDetailsSectionProps {
  bankDetails: NonNullable<Invoice['bankDetails']>
  isExport?: boolean
}

function BankDetailsSection({ bankDetails: bd, isExport = false }: BankDetailsSectionProps): JSX.Element {
  return (
    <div className="bank-section">
      <h4 className="bank-header">Bank Details for Payment:</h4>
      <div className="bank-details-list">
        <BankDetailRow label="Bank Name" value={bd.bankName} />
        <BankDetailRow label="Account Name" value={bd.accountName} />
        <BankDetailRow label="Account Number" value={bd.accountNumber} />
        <BankDetailRow label="IFSC Code" show={!isExport} value={bd.ifsc} />
        <BankDetailRow label="SWIFT Code" value={bd.swiftCode} />
        <BankDetailRow label="IBAN" value={bd.iban} />
        <BankDetailRow label="Routing Number" value={bd.routingNumber} />
        <BankDetailRow label="Sort Code" value={bd.sortCode} />
        <BankDetailRow label="Branch" value={bd.branch} />
      </div>
    </div>
  )
}

// =============================================================================
// Totals table pieces
// =============================================================================

interface DiscountRowsProps {
  invoice: Invoice
}

function DiscountRows({ invoice }: DiscountRowsProps): JSX.Element {
  return (
    <>
      <tr>
        <td>Subtotal:</td>
        <td className="amount">{formatAmount(invoice.subtotal || 0, invoice.currency)}</td>
      </tr>
      <tr>
        <td>Discount:</td>
        <td className="amount">-{formatAmount(invoice.discountAmount || 0, invoice.currency)}</td>
      </tr>
    </>
  )
}

interface RoundOffRowProps {
  amount: number
  currency: string | undefined
}

function RoundOffRow({ amount, currency }: RoundOffRowProps): JSX.Element {
  return (
    <tr>
      <td>Round Off:</td>
      <td className="amount">
        {amount > 0 ? '+' : ''}
        {formatAmount(amount, currency)}
      </td>
    </tr>
  )
}

interface TotalsTableProps {
  invoice: Invoice
  isExport: boolean
  isIntrastate: boolean
  taxBreakdown: TaxBreakdownEntry[]
}

function TotalsTable({ invoice, isExport, isIntrastate, taxBreakdown }: TotalsTableProps): JSX.Element {
  const hasDiscount = (Number(invoice.discountAmount) || 0) > 0
  return (
    <div className="totals-section">
      <table className="totals-table">
        <tbody>
          {hasDiscount && <DiscountRows invoice={invoice} />}
          <tr>
            <td>Taxable Amount:</td>
            <td className="amount">{formatAmount(invoice.taxableAmount || 0, invoice.currency)}</td>
          </tr>
          {!isExport && (
            <TaxRows currency={invoice.currency} isIntrastate={isIntrastate} taxBreakdown={taxBreakdown} />
          )}
          {!!invoice.roundOffAmount && (
            <RoundOffRow amount={invoice.roundOffAmount} currency={invoice.currency} />
          )}
          <tr className="total-row">
            <td><strong>Total Amount:</strong></td>
            <td className="amount">
              <strong>{formatAmount(invoice.totalAmount || 0, invoice.currency)}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function NotesSection({ invoice }: { invoice: Invoice }): JSX.Element | null {
  if (!(invoice.notes ?? invoice.termsConditions)) { return null }
  return (
    <div className="notes-section">
      {invoice.notes && (
        <p><strong>Notes:</strong> {invoice.notes}</p>
      )}
      {invoice.termsConditions && (
        <p><strong>Terms & Conditions:</strong> {invoice.termsConditions}</p>
      )}
    </div>
  )
}

// =============================================================================
// Public component
// =============================================================================

interface InvoiceViewTotalsProps {
  invoice: Invoice
  isVendor: boolean
  isExport: boolean
  isIntrastate: boolean
  taxBreakdown: TaxBreakdownEntry[]
}

export function InvoiceViewTotals({
  invoice,
  isVendor,
  isExport,
  isIntrastate,
  taxBreakdown,
}: InvoiceViewTotalsProps): JSX.Element {
  return (
    <>
      <TotalsTable
        invoice={invoice}
        isExport={isExport}
        isIntrastate={isIntrastate}
        taxBreakdown={taxBreakdown}
      />
      {invoice.amountInWords && (
        <div className="amount-words">
          <strong>Amount in Words:</strong> {invoice.amountInWords}
        </div>
      )}
      {!isVendor && invoice.bankDetails && (
        <BankDetailsSection bankDetails={invoice.bankDetails} isExport={isExport} />
      )}
      <NotesSection invoice={invoice} />
    </>
  )
}
